import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, FileText, Search, CreditCard, Calendar, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function SalesOrders() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ id: null, venda_id: '', cliente_id: '', cliente_nome: '', data_contabilizacao: '', valor_total: '', observacoes: '', status_pagamento: 'PENDENTE', itens: [] });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);

  useEffect(() => {
    fetchOrders();
    fetchClients();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php`);
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`);
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/produtos.php`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    const client = clients.find(c => c.cliente_id === clientId || c.id.toString() === clientId);
    setFormData(prev => ({
      ...prev,
      cliente_id: clientId,
      cliente_nome: client ? client.cliente_nome : ''
    }));
  };

  const handleAddItem = () => {
    if (!selectedProduct || itemQuantity <= 0) return;

    const product = products.find(p => p.id.toString() === selectedProduct);
    if (!product) return;

    if (itemQuantity > product.produto_quantidade) {
      showAlert('Aviso',`Quantidade indisponível. Estoque atual: ${product.produto_quantidade}`);
      return;
    }

    const price = parseFloat(product.produto_preco) || 0;

    const newItem = {
      produto_id: product.id,
      produto_nome: product.produto_nome,
      quantidade: parseInt(itemQuantity, 10),
      preco_unitario: price,
      subtotal: price * parseInt(itemQuantity, 10)
    };

    setFormData(prev => {
      const updatedItens = [...prev.itens, newItem];
      const newTotal = updatedItens.reduce((sum, item) => sum + item.subtotal, 0);
      return { ...prev, itens: updatedItens, valor_total: newTotal.toFixed(2) };
    });

    setSelectedProduct('');
    setItemQuantity(1);
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => {
      const updatedItens = prev.itens.filter((_, i) => i !== index);
      const newTotal = updatedItens.reduce((sum, item) => sum + item.subtotal, 0);
      return { ...prev, itens: updatedItens, valor_total: newTotal.toFixed(2) };
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';

    const payload = { ...formData };
    if (!payload.id && !payload.venda_id) {
      payload.venda_id = 'PED' + Math.floor(Math.random() * 100000);
    }
    payload.valor_total = parseFloat(payload.valor_total.toString().replace(',', '.')) || 0;

    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchOrders();
        fetchProducts();
      } else {
        showAlert('Aviso','Erro ao salvar pedido: ' + (result.error || 'Desconhecido'));
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleCompleteOrder = async (id) => {
    try {
      const order = orders.find(o => o.id === id);
      if(!order) return;
      const response = await fetch(`${API_BASE_URL}/vendas.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...order, status_pagamento: 'PAGO' })
      });
      const result = await response.json();
      if(result.success) {
         fetchOrders();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewDanfePdf = (vendaId) => {
    window.open(`${API_BASE_URL}/danfe_pdf.php?venda_id=${vendaId}`, '_blank');
  };

  const handleGenerateInvoice = async (id) => {
    try {
      showAlert('Processando', `Gerando NF-e 55 para o pedido #${id} via SEFAZ... Aguarde.`);
      
      const response = await fetch(`${API_BASE_URL}/emitir_nfe.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venda_id: id })
      });

      const result = await response.json();

      if (result.success) {
        showAlert(
          'NF-e 55 Gerada com Sucesso!',
          `Número da Nota: NF-e #${result.numero_nfe}\nChave de Acesso: ${result.chave_acesso}\nStatus: ${result.status.toUpperCase()}\n\n${result.motivo_sefaz}`
        );

        // Abrir DANFE PDF em nova aba automaticamente
        if (result.chave_acesso) {
          window.open(`${API_BASE_URL}/danfe_pdf.php?chave=${result.chave_acesso}`, '_blank');
        }
      } else {
        showAlert('Erro ao Gerar NF-e', result.error || 'Falha ao processar a emissão da nota fiscal.');
      }
    } catch (error) {
      console.error('Erro na emissão de NF-e:', error);
      showAlert('Erro', 'Ocorreu uma falha de conexão ao comunicar com o serviço de NF-e.');
    }
  };

  const openNewModal = () => {
    setFormData({ id: null, venda_id: '', cliente_id: '', cliente_nome: '', data_contabilizacao: '', valor_total: '', observacoes: '', status_pagamento: 'PENDENTE', itens: [] });
    setSelectedProduct('');
    setItemQuantity(1);
    setCurrentStep(1);
    setIsModalOpen(true);
  };

  const handleDeleteOrder = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este pedido? Os itens retornarão ao estoque.');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php?id=${id}`, { method: 'DELETE' });
      const result = await response.json();
      if (result.success) {
        fetchOrders();
        fetchProducts();
      } else {
        showAlert('Aviso','Erro ao excluir pedido');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleNextStep = () => {
    if (!formData.cliente_id || !formData.data_contabilizacao) {
      showAlert('Aviso',"Preencha o cliente e a data antes de avançar.");
      return;
    }
    setCurrentStep(2);
  };

  const filteredOrders = orders.filter(order => 
    (order.cliente_nome && order.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.venda_id && order.venda_id.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
    (order.id && order.id.toString().includes(searchTerm))
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pedidos de Venda</h1>
          <p className="page-subtitle">Acompanhe os pedidos, faturamentos e emissão de notas fiscais</p>
        </div>
        <button className="btn-primary" onClick={openNewModal}>
          <Plus size={18} />
          Novo Pedido
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar pedido por ID ou Cliente..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Detalhes</th>
                <th>Itens</th>
                <th>Status</th>
                <th>Ações Rapidas</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="font-medium">#{order.venda_id || order.id}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{order.cliente_nome}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)', fontWeight: '600' }}>
                        <CreditCard size={14} className="text-muted" /> R$ {Number(order.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        <Calendar size={14} /> {order.data_contabilizacao}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {order.itens && order.itens.length > 0 
                        ? order.itens.map(i => `${i.quantidade}x ${i.produto_nome}`).join(', ') 
                        : 'Sem itens'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${order.status_pagamento === 'PAGO' ? 'badge-success' : 'badge-warning'}`}>
                      {order.status_pagamento || 'PENDENTE'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ gap: '0.75rem' }}>
                      {order.status_pagamento !== 'PAGO' && (
                        <button 
                          className="btn-success" 
                          onClick={() => handleCompleteOrder(order.id)}
                          title="Marcar como Pago"
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          <CheckCircle size={14} style={{ marginRight: '0.25rem' }} /> Pagar
                        </button>
                      )}
                      {order.nfe ? (
                        <>
                          <button 
                            className="btn-outline" 
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                            onClick={() => window.open(`${API_BASE_URL}/danfe_pdf.php?chave=${order.nfe.chave_acesso}`, '_blank')}
                            title="Visualizar DANFE da NF-e"
                          >
                            <FileText size={14} style={{ marginRight: '0.25rem' }} /> Visualizar NF
                          </button>
                          <button 
                            className="btn-outline" 
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={() => window.open(`${API_BASE_URL.replace('/src/php', '')}/${order.nfe.xml_path}`, '_blank')}
                            title="Acessar XML da NF-e"
                          >
                            <FileText size={14} style={{ marginRight: '0.25rem' }} /> XML
                          </button>
                        </>
                      ) : (
                        <button 
                          className="btn-primary" 
                          onClick={() => handleGenerateInvoice(order.id)}
                          title="Gerar Nota Fiscal Eletrônica"
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          <FileText size={14} style={{ marginRight: '0.25rem' }} /> Gerar NF
                        </button>
                      )}
                      <button 
                        className="btn-outline" 
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', borderColor: 'var(--danger)', color: 'var(--danger)' }}
                        onClick={() => handleDeleteOrder(order.id)}
                        title="Excluir Pedido"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhum pedido de venda encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? `Editar Pedido - Passo ${currentStep}/2` : `Novo Pedido de Venda - Passo ${currentStep}/2`}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {currentStep === 1 && (
            <>
              <div className="form-group">
                <label>Cliente</label>
                <select className="standard-input" name="cliente_id" value={formData.cliente_id} onChange={handleClientChange} required>
                  <option value="">Selecione o Cliente...</option>
                  {clients.map(c => (
                     <option key={c.id} value={c.cliente_id || c.id}>{c.cliente_nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>Data Contabilização</label>
                  <input type="date" name="data_contabilizacao" value={formData.data_contabilizacao} onChange={handleInputChange} required className="standard-input" />
                </div>
                <div className="form-group flex-1">
                  <label>Status</label>
                  <select name="status_pagamento" value={formData.status_pagamento} onChange={handleInputChange} className="standard-input">
                    <option value="PENDENTE">Pendente</option>
                    <option value="PAGO">Pago</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Observações Adicionais</label>
                <textarea name="observacoes" value={formData.observacoes} onChange={handleInputChange} placeholder="Detalhes do pedido..." className="standard-input" rows={2}></textarea>
              </div>

              <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                <button type="button" className="btn-primary" onClick={handleNextStep}>Avançar</button>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                 <div className="form-group flex-2">
                   <label>Produto</label>
                   <select className="standard-input" value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}>
                     <option value="">Selecione um produto...</option>
                     {products.map(p => (
                       <option key={p.id} value={p.id}>{p.produto_nome} (R$ {Number(p.produto_preco || 0).toFixed(2)} | Est: {p.produto_quantidade})</option>
                     ))}
                   </select>
                 </div>
                 <div className="form-group flex-1">
                   <label>Qtd.</label>
                   <input type="number" min="1" className="standard-input" value={itemQuantity} onChange={e => setItemQuantity(e.target.value)} />
                 </div>
                 <button type="button" className="btn-primary" onClick={handleAddItem} style={{ height: '38px', padding: '0 1rem' }}>
                   Adicionar
                 </button>
              </div>

              {formData.itens.length > 0 && (
                <div className="table-responsive" style={{ border: '1px solid var(--border)', borderRadius: '6px' }}>
                  <table className="data-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Qtd</th>
                        <th>V. Unitário</th>
                        <th>Subtotal</th>
                        <th>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.itens.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.produto_nome}</td>
                          <td>{item.quantidade}</td>
                          <td>R$ {item.preco_unitario.toFixed(2)}</td>
                          <td>R$ {item.subtotal.toFixed(2)}</td>
                          <td>
                            <button type="button" className="btn-outline" style={{ padding: '0.2rem', borderColor: 'transparent', color: 'var(--danger)' }} onClick={() => handleRemoveItem(idx)}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Valor Total (Calculado)</label>
                  <input type="number" step="0.01" name="valor_total" value={formData.valor_total} readOnly className="standard-input" style={{ backgroundColor: 'var(--bg-secondary)', fontWeight: 'bold' }} />
              </div>

              <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" onClick={() => setCurrentStep(1)}>Voltar</button>
                <button type="button" className="btn-success" onClick={handleSubmit}>Salvar Pedido</button>
              </div>
            </>
          )}

        </div>
      </Modal>
    </div>
  );
}
